import { useCallback, useEffect, useState } from "react";
import { ApiClientError } from "../api/types";

interface UseAsyncDataState<T> {
  data: T | null;
  isLoading: boolean;
  /** CLEAN compat alias: `loading` is an exact alias of `isLoading`. */
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** CLEAN compat alias: `revalidate` is an exact alias of `reload`. */
  revalidate: () => void;
}

export function useAsyncData<T>(
  loader: () => Promise<T>,
): UseAsyncDataState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => {
    setTick((value) => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    loader()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof ApiClientError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Request failed",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loader, tick]);

  return { data, isLoading, loading: isLoading, error, reload, revalidate: reload };
}
